import dayjs, { Dayjs } from 'dayjs';

/**
 * The content blobs are seeded with fixed 2026 dates. These helpers move a seeded dataset forward by
 * whole months so its latest month lands on the current one, keeping each row's day of month (so a
 * rent payment on the 1st stays on the 1st). Anything that would land after today is dropped.
 */

// Dates are stored as "YYYY-MM-DD" or "YYYY-MM-DDT…Z". Only the date part is shifted, so the time
// suffix and the original format survive, and no timezone conversion can move a row by a day.
const datePart = (iso: string) => iso.slice(0, 10);

export function monthsToNow(latestIso: string, now: Dayjs = dayjs()): number {
    const latest = dayjs(datePart(latestIso));
    return (now.year() - latest.year()) * 12 + (now.month() - latest.month());
}

export function shiftIsoDate(iso: string, months: number): string {
    // dayjs clamps the day, so a 31st shifting into a 30-day month stays a valid date.
    const shifted = dayjs(datePart(iso)).add(months, 'month').format('YYYY-MM-DD');
    return `${shifted}${iso.slice(10)}`;
}

/**
 * Shifts `dateKeys` on every row by the months between the latest `dateKeys[0]` and now, then drops
 * rows whose `dateKeys[0]` is after today. Returns new rows; the input is not mutated.
 */
export function shiftRowsToNow<T extends object>(rows: T[], dateKeys: (keyof T)[], now: Dayjs = dayjs()): T[] {
    const [primary] = dateKeys;
    const dates = rows.map((row) => row[primary]).filter((d): d is T[keyof T] & string => typeof d === 'string');
    if (!dates.length) {
        return rows;
    }
    const latest = dates.reduce((max, d) => (datePart(d) > datePart(max) ? d : max));
    const months = monthsToNow(latest, now);
    const today = now.format('YYYY-MM-DD');

    return rows
        .map((row) => {
            const next = { ...row };
            dateKeys.forEach((key) => {
                const value = row[key];
                if (typeof value === 'string') {
                    next[key] = shiftIsoDate(value, months) as T[keyof T];
                }
            });
            return next;
        })
        .filter((row) => {
            const value = row[primary];
            return typeof value !== 'string' || datePart(value) <= today;
        });
}
